import {useCallback, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useApiToken} from '@/matic/hooks/useApiClient';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {getAgentsApi} from '@/matic/features/_shared/api';
import {relayKeys} from '@/matic/features/relay/query-keys';
import {getFilesApi} from '../api/files-api';
import {filesKeys} from '../query-keys';
import {
  MULTIPART_THRESHOLD_BYTES,
  SUGGESTION_CONFIDENCE_THRESHOLD,
  type ManagedFile,
} from '../types/files-types';

export type QuickDropStatus = 'uploading' | 'classifying' | 'done' | 'error';

export type QuickDropItem = {
  /** Local row key — not the File id. */
  id: string;
  filename: string;
  status: QuickDropStatus;
  /** The created File, once done — drives the tray's "View" action. The
   *  tray re-derives category/association display live via `useFile`, so
   *  no snapshot of those fields is carried on the item itself. */
  file?: ManagedFile;
  error?: string;
};

export type QuickDropAssociation = {
  objectTypeId: string;
  objectRecordId: string;
  label?: string;
};

type Options = {
  /**
   * Auto-association for dropped files — the Library passes its current scope
   * record ("throwing a file over the fence while looking at Acme files it to
   * Acme"). Omit when unscoped: files land unassociated and surface in the
   * needs-filing view, mirroring an unclassified email thread.
   */
  association?: QuickDropAssociation;
  /** Category id → name map for the tray's "Filed to X" line. */
  categoryNameById: Map<string, string>;
};

/**
 * The "throw it over the fence" upload lane: zero-question multi-file intake.
 *
 * Per file: upload bytes to staging while AI classification runs in parallel
 * (same suggestion endpoint FileUploadDialog uses), then create the File with
 * the suggested title/category auto-applied at the standard confidence
 * threshold. No dialog, no upfront questions — the returned `items` feed the
 * QuickDropTray so the user watches MATIC do the filing.
 */
export function useQuickDropUpload({association, categoryNameById}: Options) {
  const token = useApiToken();
  const tenantId = useRequiredTenantId();
  const queryClient = useQueryClient();
  const [items, setItems] = useState<QuickDropItem[]>([]);

  const patchItem = useCallback((id: string, patch: Partial<QuickDropItem>) => {
    setItems((previous) =>
      previous.map((item) => (item.id === id ? {...item, ...patch} : item))
    );
  }, []);

  const processOne = useCallback(
    async (rowId: string, file: File) => {
      const api = getFilesApi();
      const contentType = file.type || 'application/octet-stream';

      // V1 keeps the zero-question lane to single-PUT sizes; huge files still
      // have the Files-tab dialog (multipart requires an anchor association).
      if (file.size >= MULTIPART_THRESHOLD_BYTES) {
        patchItem(rowId, {
          status: 'error',
          error: association
            ? 'Over 100 MB — use Upload in the Files tab'
            : 'Over 100 MB — open a scoped Files tab and use Upload',
        });
        return;
      }

      try {
        // Upload and classification run in parallel; classification failure is
        // never fatal (the file still lands, just unclassified).
        const uploadPromise = (async () => {
          const {url, fileId} = await api.requestUploadUrl(
            file.name,
            contentType,
            file.size
          );
          await api.uploadToPresignedUrl(url, file, undefined, contentType);
          return fileId;
        })();
        const suggestionPromise = (async () => {
          try {
            return await getAgentsApi(token).getFileUploadSuggestions(
              file,
              file.name
            );
          } catch {
            return undefined;
          }
        })();

        const fileId = await uploadPromise;
        patchItem(rowId, {status: 'classifying'});
        const suggestions = await suggestionPromise;

        const confidentTitle =
          suggestions?.title?.value &&
          suggestions.title.confidence >= SUGGESTION_CONFIDENCE_THRESHOLD
            ? suggestions.title.value
            : undefined;
        const confidentCategoryId =
          suggestions?.category?.value &&
          suggestions.category.confidence >= SUGGESTION_CONFIDENCE_THRESHOLD &&
          categoryNameById.has(suggestions.category.value)
            ? suggestions.category.value
            : undefined;

        const created = await api.createFileFromStaging({
          fileId,
          name: confidentTitle ?? file.name,
          originalFilename: file.name,
          contentType,
          categoryId: confidentCategoryId,
          associations: association
            ? [
                {
                  objectTypeId: association.objectTypeId,
                  objectRecordId: association.objectRecordId,
                },
              ]
            : [],
          cui: false,
        });

        patchItem(rowId, {
          status: 'done',
          file: created,
        });
      } catch {
        patchItem(rowId, {status: 'error', error: 'Upload failed'});
      }
    },
    [association, categoryNameById, patchItem, token]
  );

  const dropFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const rows: QuickDropItem[] = files.map((file) => ({
        id: crypto.randomUUID(),
        filename: file.name,
        status: 'uploading' as const,
      }));
      setItems((previous) => [...previous, ...rows]);

      void (async () => {
        await Promise.allSettled(
          files.map(async (file, index) => processOne(rows[index].id, file))
        );
        // One refresh for the whole batch, not one per file. `refetchType:
        // 'all'` because the Files browser is commonly unmounted (or was
        // mid-fetch when it mounted) at the moment this quick-create upload
        // finishes — an active-only invalidation just marks it stale and
        // leaves the stale response showing until an unrelated remount.
        await queryClient.invalidateQueries({
          queryKey: filesKeys.files.listPrefix(tenantId),
          refetchType: 'all',
        });
        // Associated files surface in Activity via the trace query — bust it
        // too, same as every other file/note/record creation path, or a
        // linked drop won't show up there until an unrelated refetch.
        void queryClient.invalidateQueries({queryKey: relayKeys.trace.all()});
      })();
    },
    [processOne, queryClient, tenantId]
  );

  const dismissItem = useCallback((id: string) => {
    setItems((previous) => previous.filter((item) => item.id !== id));
  }, []);

  const clearItems = useCallback(() => {
    setItems([]);
  }, []);

  return {items, dropFiles, dismissItem, clearItems};
}
